import { useState } from 'react';
import { Group, SegmentedControl, Text } from '@mantine/core';
import { IconReceiptOff } from '@tabler/icons-react';
import { CsvDownloadButton } from '../components/CsvDownloadButton';
import { DateRangeFilter } from '../components/DateRangeFilter';
import { CustomerFilterSelect, ProductFilterSelect, SiteFilterSelect } from '../components/FilterSelects';
import { ReportPage } from '../components/ReportPage';
import { ReportTable, type ReportColumn } from '../components/ReportTable';
import { rangeQuery, useMarginReport } from '../api';
import { formatDate, formatMoneyCents, formatQty } from '../format';
import type { MarginByDispatchRow, MarginByInvoiceRow } from '../types';

function isDispatchRow(row: MarginByDispatchRow | MarginByInvoiceRow): row is MarginByDispatchRow {
  return 'dispatchNumber' in row;
}

/** Report #11 (BUILD_SPEC.md §13) — margin per dispatch or per invoice (price vs
 * relief WAC), toggled via `groupBy`. */
export function MarginReport() {
  const [siteId, setSiteId] = useState<string | null>(null);
  const [customerId, setCustomerId] = useState<string | null>(null);
  const [productId, setProductId] = useState<string | null>(null);
  const [from, setFrom] = useState<string | null>(null);
  const [to, setTo] = useState<string | null>(null);
  const [groupBy, setGroupBy] = useState<'dispatch' | 'invoice'>('dispatch');

  const query = useMarginReport({ siteId: siteId ?? undefined, customerId: customerId ?? undefined, productId: productId ?? undefined, from, to, groupBy });
  const rows = query.data?.items ?? [];
  const totalMargin = rows.reduce((sum, r) => sum + BigInt(r.marginCents), 0n);

  const dispatchColumns: ReportColumn<MarginByDispatchRow>[] = [
    { key: 'dispatch', header: 'Dispatch', render: (r) => r.dispatchNumber },
    { key: 'dispatchedAt', header: 'Dispatched', render: (r) => formatDate(r.dispatchedAt) },
    { key: 'customer', header: 'Customer', render: (r) => r.customerName },
    { key: 'product', header: 'Product', render: (r) => r.productSku },
    { key: 'qty', header: 'Qty', align: 'right', render: (r) => formatQty(r.qty) },
    { key: 'sale', header: 'Sale value', align: 'right', render: (r) => formatMoneyCents(r.saleValueCents) },
    { key: 'cost', header: 'Cost (WAC)', align: 'right', render: (r) => formatMoneyCents(r.costCents) },
    { key: 'margin', header: 'Margin', align: 'right', render: (r) => formatMoneyCents(r.marginCents) },
  ];

  const invoiceColumns: ReportColumn<MarginByInvoiceRow>[] = [
    { key: 'invoice', header: 'Invoice', render: (r) => r.invoiceNumber },
    { key: 'issueDate', header: 'Issued', render: (r) => formatDate(r.issueDate) },
    { key: 'customer', header: 'Customer', render: (r) => r.customerName },
    { key: 'sale', header: 'Sale value', align: 'right', render: (r) => formatMoneyCents(r.saleValueCents) },
    { key: 'cost', header: 'Cost (WAC)', align: 'right', render: (r) => formatMoneyCents(r.costCents) },
    { key: 'margin', header: 'Margin', align: 'right', render: (r) => formatMoneyCents(r.marginCents) },
  ];

  return (
    <ReportPage
      title="Margin per dispatch/invoice"
      description="Sale price vs weighted-average cost at relief, per dispatch or rolled up per invoice."
      action={
        <CsvDownloadButton
          path="/reporting/margin"
          query={{
            siteId: siteId ?? undefined,
            customerId: customerId ?? undefined,
            productId: productId ?? undefined,
            ...rangeQuery(from, to),
            groupBy,
          }}
          filename={`margin-by-${groupBy}.csv`}
          description="Exports this margin breakdown, matching your filters, as a CSV file"
        />
      }
    >
      <Group gap="md" wrap="wrap" align="flex-end">
        <SegmentedControl
          value={groupBy}
          onChange={(v) => setGroupBy(v as 'dispatch' | 'invoice')}
          data={[
            { label: 'By dispatch', value: 'dispatch' },
            { label: 'By invoice', value: 'invoice' },
          ]}
        />
        <SiteFilterSelect value={siteId} onChange={setSiteId} label="Site" w={200} />
        <CustomerFilterSelect value={customerId} onChange={setCustomerId} label="Customer" w={220} />
        {groupBy === 'dispatch' && <ProductFilterSelect value={productId} onChange={setProductId} label="Product" w={220} />}
        <DateRangeFilter from={from} to={to} onFromChange={setFrom} onToChange={setTo} />
      </Group>

      {rows.length > 0 && (
        <Text size="sm" c="dimmed">
          Total margin: <strong>{formatMoneyCents(totalMargin)}</strong> across {rows.length} row{rows.length === 1 ? '' : 's'}.
        </Text>
      )}

      {groupBy === 'dispatch' ? (
        <ReportTable
          columns={dispatchColumns}
          rows={rows.filter(isDispatchRow)}
          getRowKey={(r) => r.dispatchId}
          isLoading={query.isLoading}
          isError={query.isError}
          errorMessage="Failed to load the margin report."
          emptyMessage="No posted dispatches for the selected filters"
          emptyHint="Try a different customer, product or date range."
          emptyIcon={IconReceiptOff}
        />
      ) : (
        <ReportTable
          columns={invoiceColumns}
          rows={rows.filter((r): r is MarginByInvoiceRow => !isDispatchRow(r))}
          getRowKey={(r) => r.invoiceId}
          isLoading={query.isLoading}
          isError={query.isError}
          errorMessage="Failed to load the margin report."
          emptyMessage="No invoices for the selected filters"
          emptyHint="Try a different customer or date range."
          emptyIcon={IconReceiptOff}
        />
      )}
    </ReportPage>
  );
}
